import { zodResolver } from '@hookform/resolvers/zod';
import { Search, X } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/Select';
import { useSearchUsers } from '@/hooks/useSearchUsers';
import { SearchParams } from '@/services/UserService';

const searchSchema = z.object({
  searchValue: z.string().min(1),
  filterBy: z.enum(['userId', 'name', 'email', 'customerId']),
});

type SearchFormData = z.infer<typeof searchSchema>;

interface SearchUsersFormProps {
  searchParams: SearchParams;
  onSearchParams: (value: SearchParams) => void;
}

export function SearchUsersForm({
  searchParams,
  onSearchParams,
}: SearchUsersFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { isValid, isSubmitting, errors },
  } = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      searchValue: '',
      filterBy: 'name',
    },
  });

  const { isLoading, searchUser } = useSearchUsers(searchParams);

  const searchValue = watch('searchValue');

  const clearParams = useCallback(() => {
    onSearchParams({
      ...searchParams,
      searchValue: '',
    });

    reset({ searchValue: undefined });
  }, [onSearchParams, reset, searchParams]);

  const onSubmit = handleSubmit((data) => {
    onSearchParams({
      ...searchParams,
      filterBy: data.filterBy,
      searchValue: data.searchValue,
    });
  });

  useEffect(() => {
    async function loadUsers() {
      await searchUser();
    }

    loadUsers();
  }, [searchUser, searchParams]);

  useEffect(() => {
    if (searchParams.searchValue && !searchValue) {
      clearParams();
    }
  }, [clearParams, searchParams.searchValue, searchValue]);

  return (
    <div>
      <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <FormGroup>
            <Input
              id="searchValue"
              className="w-full pr-10"
              placeholder="Search users..."
              {...register('searchValue')}
            />

            {searchParams.searchValue && (
              <button
                type="button"
                onClick={clearParams}
                className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </FormGroup>
        </div>

        <FormGroup error={{ message: errors.filterBy?.message, font: 'sm' }}>
          <Controller
            name="filterBy"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Filter by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="userId">User ID</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="customerId">Customer ID</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </FormGroup>

        <Button
          disabled={!isValid || isSubmitting}
          type="submit"
          className={`flex items-center gap-2 transition-opacity duration-200 ${
            isLoading ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          {isLoading ? (
            <Search className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
          Search
        </Button>
      </form>
    </div>
  );
}
